import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import BriefingCard from "@/components/BriefingCard";
import { ChevronLeft, ChevronRight, Mailbox } from "lucide-react";

const PER_PAGE = 5;

export default function BriefingsPage() {
  const [items, setItems] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [err, setErr] = useState("");

  const load = async (p) => {
    setErr("");
    try {
      const { data } = await api.get("/briefings/history", {
        params: { page: p, per_page: PER_PAGE },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      setItems([]);
    }
  };

  useEffect(() => {
    load(page);
  }, [page]);

  if (items === null) {
    return (
      <div className="max-w-3xl mx-auto w-full p-6 lg:p-10 text-sm text-slate-500" data-testid="briefings-loading">
        Loading briefings…
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div className="max-w-3xl mx-auto w-full p-6 lg:p-10 space-y-6" data-testid="briefings-page">
      <header>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
          <Mailbox className="h-3.5 w-3.5" /> Archive
        </div>
        <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900">
          Briefings
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Every Monday briefing sent to your inbox. A permanent record for quarter-end reviews.
        </p>
      </header>

      {err && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="briefings-error">
          {err}
        </div>
      )}

      {items.length === 0 && !err && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center" data-testid="briefings-empty">
          <div className="mx-auto h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
            <Mailbox className="h-5 w-5 text-slate-500" />
          </div>
          <div className="mt-4 text-sm font-semibold text-slate-900">No briefings yet</div>
          <div className="mt-1 text-sm text-slate-500">
            Your first Monday briefing will appear here once it&apos;s sent. You can trigger one
            manually from Settings.
          </div>
        </div>
      )}

      <div className="space-y-6">
        {items.map((br) => (
          <div key={br.resend_id || br.sent_at} className="space-y-2">
            <div className="text-xs text-slate-500 flex items-center gap-2">
              <span className="font-semibold text-slate-700">
                {new Date(br.sent_at).toLocaleDateString(undefined, {
                  weekday: "short",
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </span>
              <span>·</span>
              <span className="truncate" data-testid="briefing-subject">{br.subject}</span>
            </div>
            <BriefingCard briefing={br} cur={br.briefing_json?.currency} />
          </div>
        ))}
      </div>

      {total > PER_PAGE && (
        <nav
          className="flex items-center justify-between border-t border-slate-200 pt-4"
          data-testid="briefings-pagination"
        >
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="briefings-prev"
          >
            <ChevronLeft className="h-4 w-4" /> Previous
          </button>
          <div className="text-xs text-slate-500">
            Page <span className="font-semibold text-slate-900">{page}</span> of{" "}
            <span className="font-semibold text-slate-900">{totalPages}</span> · {total} total
          </div>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={!hasMore}
            className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="briefings-next"
          >
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </nav>
      )}
    </div>
  );
}
